import type { CustomTool } from "../schema/tool-schema";
import { apiFetch, apiUrl } from "./api";
import { maybeUpsellPlanLimit, PlanLimitError } from "./plan-limit-store";

/**
 * Client for the user's template store (`/api/templates/*`). Cookie-authenticated.
 * The client owns ids, so writes are idempotent PUT upserts. `document-store.ts`
 * keeps a React Query cache and calls these in the background to mirror writes and
 * hydrate from the server (mirroring documents).
 *
 * A template's `scope` decides its library: "personal" (private) or "workspace"
 * (shared with the org). Platform templates are read-only and only ever created
 * via the admin API, never these helpers.
 */

/** The writable scopes a user can save into (excludes read-only "platform"). */
export type WritableScope = "personal" | "workspace";

export type CustomToolPayload = Omit<CustomTool, "id" | "createdAt" | "updatedAt">;

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await apiFetch(apiUrl(path), {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
    ...init,
  });
  if (res.status === 204) return undefined as T;
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    const message =
      data && typeof data === "object" && "error" in data
        ? String((data as { error: unknown }).error)
        : `Request failed (${res.status})`;
    // A plan-quota 403 becomes the upgrade dialog; signal it so callers can skip
    // a redundant error toast.
    if (maybeUpsellPlanLimit(res.status, data)) throw new PlanLimitError(message);
    throw new Error(message);
  }
  return data as T;
}

/** All of the owner's custom tools from the server. */
export async function fetchTools(): Promise<CustomTool[]> {
  const data = await req<{ items: CustomTool[] }>("/api/templates");
  return data.items;
}

/** Upserts a custom tool by id. */
export async function putTool(id: string, payload: CustomToolPayload): Promise<CustomTool> {
  const data = await req<{ item: CustomTool }>(`/api/templates/${id}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
  return data.item;
}

/** Deletes a custom tool by id (idempotent for the client). */
export function deleteToolRemote(id: string): Promise<void> {
  return req<void>(`/api/templates/${id}`, { method: "DELETE" });
}

/**
 * Copies a template (personal / workspace / platform) into `scope` — powers
 * "Duplicate", "Share to workspace", and "Copy to personal". Returns the new tool.
 */
export async function duplicateToolRemote(id: string, scope: WritableScope): Promise<CustomTool> {
  const data = await req<{ item: CustomTool }>(`/api/templates/${id}/duplicate`, {
    method: "POST",
    body: JSON.stringify({ scope }),
  });
  return data.item;
}
